import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { createForm } from "@formily/core";
import SchemaFormView from "../../../components/Schema/SchemaFormView";
import { SchemaService, DynamicModuleService } from "../../../services/APIs";
import { AppLayout } from "@project/components/AppLayout";
import { notify } from "../../../components/common/Notification";
import { Modal, Spin, Popconfirm } from "antd";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  Search,
  Send,
  FileText,
  Sparkles,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Mail,
  TrendingUp,
  IndianRupee,
  Brain,
  Plus,
  Trash2,
  Pencil,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { toast } from "@project/hooks/use-toast";
import { useAIAction } from "@project/hooks/useAIAction";
import AIResultDisplay from "@project/components/AIResultDisplay";

const formatDate = (val: string | undefined) => {
  if (!val) return "-";
  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  }
  return val;
};

const statusConfig: Record<string, { icon: any; class: string }> = {
  Draft: { icon: FileText, class: "text-muted-foreground bg-muted" },
  Sent: {
    icon: Send,
    class: "text-[hsl(var(--status-posted))] bg-[hsl(var(--status-posted-bg))]",
  },
  Accepted: {
    icon: CheckCircle2,
    class: "text-[hsl(var(--status-active))] bg-[hsl(var(--status-active-bg))]",
  },
  Rejected: {
    icon: XCircle,
    class: "text-destructive bg-[hsl(var(--status-filled-bg))]",
  },
  Negotiation: {
    icon: TrendingUp,
    class: "text-[hsl(var(--status-onhold))] bg-[hsl(var(--status-onhold-bg))]",
  },
  Expired: {
    icon: Clock,
    class: "text-[hsl(var(--status-closed))] bg-[hsl(var(--status-closed-bg))]",
  },
};

const OffersPage = () => {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [createOpen, setCreateOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editOfferId, setEditOfferId] = useState<string | null>(null);
  const [editOfferData, setEditOfferData] = useState<any>(null);
  const [aiSalaryOpen, setAiSalaryOpen] = useState(false);
  const aiSalary = useAIAction();
  const [page] = useState(1);
  const PAGE_SIZE = 50;

  // Fetch offers from API
  const {
    data: offersData,
    isLoading: offersLoading,
    refetch: refetchOffers,
  } = useQuery({
    queryKey: ["offers-data", page, PAGE_SIZE],
    queryFn: () =>
      DynamicModuleService.getGridCoByConfig("Add Offer", page, PAGE_SIZE, [], {}),
  });

  const offers: any[] = offersData?.results ?? [];

  // Fetch schema for create form
  const { data: offerSchemaInfo, isLoading: offerSchemaLoading } = useQuery({
    queryKey: ["schema", "Add Offer"],
    queryFn: () => SchemaService.getSchemaByName("Add Offer"),
  });

  // Build candidate ID → name map from schema enum
  const candidateMap: Record<string, string> = useMemo(() => {
    const schema = offerSchemaInfo?.json ?? offerSchemaInfo;
    const enumOptions: { label: string; value: string | number }[] =
      schema?.schema?.properties?.candidate?.enum ?? [];
    const map: Record<string, string> = {};
    enumOptions.forEach((opt: any) => {
      map[String(opt.value)] = opt.label;
    });
    return map;
  }, [offerSchemaInfo]);

  const createForm_ = useMemo(() => {
    const f = createForm({});
    if (editMode && editOfferData) {
      f.setInitialValues(editOfferData);
    }
    return f;
  }, [createOpen]);

  const tabs = [
    { label: "All", count: offers.length },
    { label: "Draft", count: offers.filter(o => o.status === "Draft").length },
    { label: "Sent", count: offers.filter(o => o.status === "Sent").length },
    { label: "Negotiation", count: offers.filter(o => o.status === "Negotiation").length },
    { label: "Accepted", count: offers.filter(o => o.status === "Accepted").length },
    { label: "Rejected", count: offers.filter(o => o.status === "Rejected").length },
  ];

  const filtered = (
    activeTab === "All" ? offers : offers.filter((o: any) => o.status === activeTab)
  ).filter((o: any) => {
    if (!search) return true;
    const lower = search.toLowerCase();
    const candidateName = candidateMap[String(o.candidate)] ?? o.candidate ?? "";
    return (
      candidateName.toLowerCase().includes(lower) ||
      o.job_title?.toLowerCase().includes(lower) ||
      o.status?.toLowerCase().includes(lower)
    );
  });

  const handleEditOffer = async (offer: any) => {
    try {
      const offerData = await DynamicModuleService.getItemById("Add Offer", offer.id);
      setEditMode(true);
      setEditOfferId(offer.id);
      setEditOfferData(offerData);
      setCreateOpen(true);
    } catch (error) {
      console.error("Error fetching offer:", error);
      notify.error("Error", "Could not load offer data.");
    }
  };

  const handleCreateOffer = async () => {
    try {
      await createForm_.validate();
    } catch {
      return;
    }
    try {
      if (editMode && editOfferId) {
        await DynamicModuleService.updateItem("Add Offer", editOfferId, createForm_.values);
        toast({ title: "Offer Updated" });
      } else {
        await DynamicModuleService.createItem("Add Offer", createForm_.values);
        toast({ title: "Offer Created" });
      }
      setCreateOpen(false);
      setEditMode(false);
      setEditOfferId(null);
      setEditOfferData(null);
      refetchOffers();
    } catch (error) {
      console.error("Error saving offer:", error);
      notify.error(editMode ? "Update Failed" : "Create Failed", "Could not save the offer.");
    }
  };

  const handleDeleteOffer = async (offer: any) => {
    try {
      await DynamicModuleService.deleteItem("Add Offer", offer.id);
      refetchOffers();
      toast({ title: "Offer Deleted" });
    } catch (error) {
      console.error("Error deleting offer:", error);
      notify.error("Delete Failed", "Could not delete the offer.");
    }
  };

  const stats = [
    {
      label: "Total Offers",
      value: offers.length,
      icon: FileText,
      accent: "hsl(var(--primary))",
      bg: "hsl(var(--primary) / 0.08)",
    },
    {
      label: "Accepted",
      value: offers.filter((o: any) => o.status === "Accepted").length,
      icon: CheckCircle2,
      accent: "hsl(160 84% 39%)",
      bg: "hsl(160 84% 39% / 0.08)",
    },
    {
      label: "In Negotiation",
      value: offers.filter((o: any) => o.status === "Negotiation").length,
      icon: TrendingUp,
      accent: "hsl(38 92% 50%)",
      bg: "hsl(38 92% 50% / 0.08)",
    },
    {
      label: "Pending",
      value: offers.filter((o: any) => o.status === "Sent").length,
      icon: Clock,
      accent: "hsl(199 89% 48%)",
      bg: "hsl(199 89% 48% / 0.08)",
    },
  ];

  return (
    <AppLayout>
      <motion.div
        className="space-y-4"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div
          variants={item}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="font-display text-lg font-bold tracking-tight text-foreground">
              Offers
            </h1>
            <p className="mt-0.5 hidden text-[12px] text-muted-foreground sm:block">
              Manage offers, negotiations, and acceptance tracking
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="ai-btn h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
              onClick={async () => {
                setAiSalaryOpen(true);
                await aiSalary.run(`You are a compensation analyst AI. Analyze these offer details:

${offers.map((o: any) => `- Candidate: ${candidateMap[String(o.candidate)] ?? o.candidate}, Position=${o.job_title ?? "-"}, Offered=${o.salary_offered ?? "-"}, Status=${o.status}`).join("\n")}

Provide:
1. **Market Analysis** - how the offers compare to market rates (Indian market)
2. **Salary Gap Analysis** - offered vs expected
3. **Negotiation Strategy** - tips for candidates in negotiation
4. **Budget Optimization** - balance quality hires with budget
5. **Recommendations** - specific salary adjustments`);
              }}
            >
              <Brain className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">AI Salary Advisor</span>
            </Button>
            <Button
              size="sm"
              onClick={() => setCreateOpen(true)}
              className="btn-glow h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Create Offer</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stats.map(s => (
            <motion.div key={s.label} variants={item} className="section-card p-3 sm:p-4">
              <div className="mb-2 flex items-center gap-2">
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-lg"
                  style={{ background: s.bg }}
                >
                  <s.icon className="h-3.5 w-3.5" style={{ color: s.accent }} />
                </div>
              </div>
              <p className="font-display text-xl font-bold text-foreground">{s.value}</p>
              <p className="mt-0.5 text-[10px] text-muted-foreground">{s.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <motion.div
          variants={item}
          className="no-scrollbar flex items-center gap-0.5 overflow-x-auto rounded-xl bg-muted/50 p-0.5"
        >
          {tabs.map(tab => (
            <button
              key={tab.label}
              onClick={() => setActiveTab(tab.label)}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-[12px] font-medium transition-all ${activeTab === tab.label ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              {tab.label}
              <span
                className={`rounded-md px-1.5 py-0 text-[10px] font-bold ${activeTab === tab.label ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </motion.div>

        {/* Search */}
        <motion.div variants={item}>
          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search offers..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 rounded-lg border-border/60 bg-card pl-8 text-[12px]"
            />
          </div>
        </motion.div>

        {/* Table */}
        <motion.div variants={item}>
          {offersLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Spin size="large" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="section-card p-8 text-center">
              <Send className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
              <p className="text-[13px] text-muted-foreground">No offers found</p>
            </div>
          ) : (
            <div className="section-card overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableHead className="pl-5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Candidate</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Position</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Salary Offered</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Status</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Date</TableHead>
                      <TableHead className="w-[80px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((o: any) => {
                      const candidateName = candidateMap[String(o.candidate)] ?? o.candidate ?? "-";
                      const avatarText = candidateName
                        .split(" ")
                        .slice(0, 2)
                        .map((w: string) => w[0])
                        .join("")
                        .toUpperCase() || "??";
                      const sc = statusConfig[o.status] ?? statusConfig["Draft"];
                      return (
                        <TableRow key={o.id} className="table-row-hover">
                          <TableCell className="pl-5">
                            <div className="flex items-center gap-2.5">
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow">
                                <span className="text-[9px] font-bold text-white">{avatarText}</span>
                              </div>
                              <p className="text-[12px] font-semibold text-foreground">{candidateName}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-[12px] text-foreground">
                            {o.job_title ?? o.job ?? "-"}
                          </TableCell>
                          <TableCell className="text-[12px] font-semibold text-foreground">
                            <span className="flex items-center gap-1">
                              <IndianRupee className="h-3 w-3" />
                              {o.salary_offered ?? o.ctc ?? "-"}
                            </span>
                          </TableCell>
                          <TableCell>
                            {o.status ? (
                              <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold ${sc.class}`}>
                                <sc.icon className="h-3 w-3" />
                                {o.status}
                              </span>
                            ) : "-"}
                          </TableCell>
                          <TableCell className="text-[12px] text-muted-foreground">
                            {formatDate(o.date ?? o.sent_date ?? o.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleEditOffer(o)}
                                className="rounded-lg p-2 transition-colors hover:bg-muted"
                                title="Edit"
                              >
                                <Pencil className="h-4 w-4 text-muted-foreground" />
                              </button>
                              <Popconfirm
                                title="Delete this offer?"
                                onConfirm={() => handleDeleteOffer(o)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <button
                                  className="rounded-lg p-2 transition-colors hover:bg-muted"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                                </button>
                              </Popconfirm>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* Create / Edit Offer Modal */}
      <Modal
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          setEditMode(false);
          setEditOfferId(null);
          setEditOfferData(null);
        }}
        title={editMode ? "Edit Offer" : "Create Offer"}
        width={600}
        footer={[
          <Button key="cancel" variant="outline" onClick={() => {
            setCreateOpen(false);
            setEditMode(false);
            setEditOfferId(null);
            setEditOfferData(null);
          }}>
            Cancel
          </Button>,
          <Button key="save" onClick={handleCreateOffer} className="btn-glow ml-2">
            {editMode ? "Update Offer" : "Create Offer"}
          </Button>,
        ]}
        destroyOnClose
        styles={{
          body: {
            maxHeight: "70vh",
            overflowY: "auto",
            paddingTop: 8,
            scrollbarWidth: "none",
          },
        }}
      >
        {offerSchemaLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : offerSchemaInfo ? (
          <SchemaFormView
            form={createForm_}
            schemaJson={offerSchemaInfo?.json ?? offerSchemaInfo}
          />
        ) : (
          <div className="flex h-40 items-center justify-center text-[13px] text-muted-foreground">
            Schema "Add Offer" not found. Please configure it in admin.
          </div>
        )}
      </Modal>

      {/* AI Salary Advisor Dialog */}
      <Dialog open={aiSalaryOpen} onOpenChange={setAiSalaryOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Brain className="h-5 w-5 text-primary" />
              AI Salary Advisor
            </DialogTitle>
          </DialogHeader>
          {aiSalary.loading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">Analyzing compensation data...</p>
            </div>
          ) : (
            <AIResultDisplay content={aiSalary.result} />
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default OffersPage;
